import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Prescription } from '@/types';
import { supabase } from '@/lib/supabase';
import { getCurrentHospitalId } from '@/lib/hospitalContext';
import { generatePrescriptionId } from '@/lib/utils/patientId';

// Define a type for the prescription data required when creating a new prescription
export type NewPrescriptionData = Omit<Prescription, 'id' | 'createdAt' | 'status'>;

// Define status types for better type safety
export type PrescriptionStatus = 'draft' | 'issued';

// Define the store state interface for better type safety
interface PrescriptionsState {
  prescriptions: Prescription[];
  
  // Actions
  addPrescription: (prescription: NewPrescriptionData) => string;
  updatePrescription: (prescription: Prescription) => void;
  removePrescription: (id: string) => void;
  
  // Selectors
  getPrescriptionsByPatient: (patientId: string) => Prescription[];
  getPrescriptionsByDoctor: (doctorId: string) => Prescription[];
  getPrescriptionById: (id: string) => Prescription | undefined;
  // Fetch all prescriptions from Supabase
  fetchPrescriptions: () => Promise<void>;
}

export const usePrescriptionsStore = create<PrescriptionsState>()(
  persist(
    (set, get) => ({
      prescriptions: [],
      
      // Add a new prescription
      addPrescription: (prescriptionData) => {
        const currentPrescriptions = get().prescriptions;
        const id = generatePrescriptionId(currentPrescriptions);
        const newPrescription: Prescription = {
          ...prescriptionData,
          id,
          createdAt: new Date().toISOString()
        };
        
        set((state) => ({
          prescriptions: [...state.prescriptions, newPrescription]
        }));
        
        return id;
      },
      
      // Update an existing prescription
      updatePrescription: (updatedPrescription) => {
        set((state) => ({
          prescriptions: state.prescriptions.map((prescription) =>
            prescription.id === updatedPrescription.id ? updatedPrescription : prescription
          )
        }));
      },
      
      // Remove a prescription by ID
      removePrescription: (id) => {
        set((state) => ({
          prescriptions: state.prescriptions.filter((prescription) => prescription.id !== id)
        }));
      },
      
      // Get prescriptions by patient ID
      getPrescriptionsByPatient: (patientId) => {
        const { prescriptions } = get();
        return prescriptions
          .filter(prescription => prescription.patientId === patientId)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },
      
      // Get prescriptions by doctor ID
      getPrescriptionsByDoctor: (doctorId) => {
        const { prescriptions } = get();
        return prescriptions
          .filter(prescription => prescription.doctorId === doctorId)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },
      
      // Get a single prescription by ID
      getPrescriptionById: (id) => {
        const { prescriptions } = get();
        return prescriptions.find(prescription => prescription.id === id);
      },
      // Fetch all prescriptions from Supabase and update the store
      fetchPrescriptions: async () => {
        const hospitalId = await getCurrentHospitalId();
        if (!hospitalId) {
          set({ prescriptions: [] });
          return;
        }
        const { data, error } = await supabase
          .from('prescriptions')
          .select('*')
          .eq('hospital_id', hospitalId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        set({ prescriptions: data || [] });
      },
    }),
    {
      name: 'prescriptions-storage',
    }
  )
);

// Find existing draft for a patient
export async function findExistingDraft(patientId: string, doctorId: string) {
  try {
    // Use a more specific select to avoid 406 errors
    const { data, error } = await supabase
      .from('prescriptions')
      .select('id, patient_id, doctor_id, medications, instructions, status, hospital_id, created_at, updated_at, notes, chief_complaints, complaints_duration, clinical_findings, diagnosis, lab_tests, follow_up_date')
      .eq('patient_id', patientId)
      .eq('doctor_id', doctorId)
      .eq('status', 'draft')
      .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors when no row found
    
    if (error) {
      // Handle 406 errors gracefully - might be due to RLS or query format
      if (error.code === 'PGRST116' || error.status === 406) {
        // No draft found or query format issue - return null
        console.log('No draft found or query issue:', error.message);
        return null;
      }
      console.error('Error finding existing draft:', error);
      throw error;
    }
    
    return data;
  } catch (error: any) {
    // Handle 406 errors gracefully
    if (error?.status === 406 || error?.code === 'PGRST406') {
      console.log('406 error finding draft (likely RLS or query format issue), returning null');
      return null;
    }
    console.error('Error in findExistingDraft:', error);
    throw error;
  }
}

// Update existing draft
export async function updateExistingDraft(draftId: string, prescription: Partial<Prescription>) {
  try {
    
    const updateData: any = {
      medications: JSON.stringify(prescription.medications || []),
      instructions: prescription.instructions || 'Draft prescription - instructions to be added',
      updated_at: new Date().toISOString(),
    };

    // Add status if provided (for converting draft to issued)
    if (prescription.status !== undefined) {
      updateData.status = prescription.status;
    }

    // Add optional fields - include them even if empty to ensure they're saved
    // Use !== undefined to check if field was provided (allows empty strings to be saved)
    if (prescription.notes !== undefined) updateData.notes = prescription.notes || '';
    if (prescription.chief_complaints !== undefined) updateData.chief_complaints = prescription.chief_complaints || '';
    if (prescription.complaints_duration !== undefined) updateData.complaints_duration = prescription.complaints_duration || '';
    if (prescription.clinical_findings !== undefined) updateData.clinical_findings = prescription.clinical_findings || '';
    if (prescription.diagnosis !== undefined) updateData.diagnosis = prescription.diagnosis || '';
    // Always save lab_tests, even if empty array
    if (prescription.lab_tests !== undefined) {
      updateData.lab_tests = Array.isArray(prescription.lab_tests) && prescription.lab_tests.length > 0 
        ? JSON.stringify(prescription.lab_tests) 
        : '[]';
    }
    if (prescription.follow_up_date !== undefined) updateData.follow_up_date = prescription.follow_up_date;
    if (prescription.vitals !== undefined) updateData.vitals = JSON.stringify(prescription.vitals || {});


    const { data, error } = await supabase
      .from('prescriptions')
      .update(updateData)
      .eq('id', draftId)
      .select();

    if (error) {
      console.error('Error updating draft:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in updateExistingDraft:', error);
    throw error;
  }
}

export async function savePrescriptionToBackend(prescription: Partial<Prescription>) {
  try {
    // Check if there's an existing draft for this patient/doctor combination
    const existingDraft = await findExistingDraft(prescription.patientId!, prescription.doctorId!);
    
    if (existingDraft) {
      
      // If we're converting a draft to issued, update the existing draft
      if (prescription.status === 'issued') {
        return await updateExistingDraft(existingDraft.id, {
          ...prescription,
          status: 'issued',
          updatedAt: new Date().toISOString()
        });
      } else {
        // If we're updating a draft, just update it
        console.log('Updating existing draft');
        return await updateExistingDraft(existingDraft.id, prescription);
      }
    }

    // No existing draft found, create a new prescription
    console.log('No existing draft found, creating new prescription');

    // Generate sequential ID for the prescription
    const { data: existingPrescriptions } = await supabase
      .from('prescriptions')
      .select('id, created_at')
      .order('created_at', { ascending: false });

    // Import generatePrescriptionId function
    const { generatePrescriptionId } = await import('../utils/patientId');
    const prescriptionId = generatePrescriptionId(existingPrescriptions || []);

    // Get current hospital_id for multi-tenant isolation
    let hospitalId: string | null = null;
    try {
      const { useHospital } = await import('../hooks/useHospital');
      const hospital = useHospital.getState().currentHospital;
      hospitalId = hospital?.id || null;
      console.log('🏥 Current hospital_id for prescription:', hospitalId);
    } catch (e) {
      console.warn('⚠️ Could not get hospital_id for prescription:', e);
    }

    // Create a minimal insert data object with only required fields
    const insertData: any = {
      id: prescriptionId,
      patient_id: prescription.patientId,
      doctor_id: prescription.doctorId,
      hospital_id: hospitalId, // Required for RLS policy
      medications: JSON.stringify(prescription.medications || []),
      instructions: prescription.instructions || 'Draft prescription - instructions to be added',
      status: prescription.status || 'draft',
      created_at: prescription.createdAt || new Date().toISOString(),
      updated_at: prescription.updatedAt || new Date().toISOString(),
    };

    // Add optional fields - include them even if empty to ensure they're saved
    // Use !== undefined to check if field was provided (allows empty strings to be saved)
    if (prescription.notes !== undefined) insertData.notes = prescription.notes || '';
    if (prescription.chief_complaints !== undefined) insertData.chief_complaints = prescription.chief_complaints || '';
    if (prescription.complaints_duration !== undefined) insertData.complaints_duration = prescription.complaints_duration || '';
    if (prescription.clinical_findings !== undefined) insertData.clinical_findings = prescription.clinical_findings || '';
    if (prescription.diagnosis !== undefined) insertData.diagnosis = prescription.diagnosis || '';
    // Always save lab_tests, even if empty array
    if (prescription.lab_tests !== undefined) {
      insertData.lab_tests = Array.isArray(prescription.lab_tests) && prescription.lab_tests.length > 0 
        ? JSON.stringify(prescription.lab_tests) 
        : '[]';
    }
    if (prescription.follow_up_date !== undefined) insertData.follow_up_date = prescription.follow_up_date;
    if (prescription.vitals !== undefined) insertData.vitals = JSON.stringify(prescription.vitals || {});

    console.log('Insert data for prescription:', {
      id: prescriptionId,
      status: prescription.status,
      medications: prescription.medications,
      medicationsJson: JSON.stringify(prescription.medications || [])
    });

    console.log('Saving prescription with ID:', prescriptionId);
    console.log('Final insert data:', insertData);

    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .insert([insertData]);

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }
      
      console.log('Prescription saved successfully:', data);
      return data;
    } catch (dbError) {
      console.error('Database operation failed:', dbError);
      throw dbError;
    }
  } catch (error) {
    console.error('Error saving prescription:', error);
    throw error;
  }
}

// Test database connection
export async function testDatabaseConnection() {
  try {
    console.log('Testing database connection...');
    const { data, error } = await supabase
      .from('prescriptions')
      .select('count')
      .limit(1);
    
    if (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
    
    console.log('Database connection test successful');
    return true;
  } catch (error) {
    console.error('Database connection test error:', error);
    return false;
  }
}

export async function fetchPrescriptionsByPatient(patientId: string) {
  try {
    console.log('Fetching prescriptions for patient:', patientId);
    
    const { data, error } = await supabase
      .from('prescriptions')
      .select('id, patient_id, doctor_id, medications, instructions, status, hospital_id, created_at, updated_at, notes, chief_complaints, complaints_duration, clinical_findings, diagnosis, lab_tests, follow_up_date')
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Error fetching prescriptions:', error);
      throw error;
    }
    
    console.log('Fetched prescriptions:', data);
    
    // Parse medications JSON if needed
    return data?.map((item: any) => ({
      ...item,
      medications: typeof item.medications === 'string' ? JSON.parse(item.medications) : item.medications,
      // Handle lab_tests - can be JSONB (object), JSON string, or null/undefined
      lab_tests: item.lab_tests 
        ? (typeof item.lab_tests === 'string' 
          ? (item.lab_tests.trim().startsWith('[') || item.lab_tests.trim().startsWith('{') 
            ? JSON.parse(item.lab_tests) 
            : item.lab_tests.split(',').map((t: string) => t.trim()).filter(Boolean))
          : (Array.isArray(item.lab_tests) ? item.lab_tests : []))
        : [],
      // Ensure all text fields are strings (handle null/undefined)
      chief_complaints: item.chief_complaints || '',
      complaints_duration: item.complaints_duration || '',
      clinical_findings: item.clinical_findings || '',
      diagnosis: item.diagnosis || '',
      notes: item.notes || '',
      patientId: item.patient_id,
      doctorId: item.doctor_id,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      status: item.status,
      id: item.id,
    })) || [];
  } catch (error) {
    console.error('Error in fetchPrescriptionsByPatient:', error);
    throw error;
  }
}
